import styles from './contract-licenses.module.scss';
import { ValueFormatterParams } from 'ag-grid-community';
import { Col, Row } from 'reactstrap';
import { useSingleContractDispatch } from '../contract-details-reducer/contract-details-reducer';
import { calcItemRecurringPrice, flatten, isFutureLicense, isNewLicense } from '../../helpers/helpers';
import Icon from '../../icon/icon';
import { useState } from 'react';
import { PrimaryButton } from '../../button/button';
import { booleanHeader, CreateHeaders, currencyHeader, HeaderSizeEnum, textHeader, toCurrency } from '../../table-helpers/table-helpers';
import ContractChangeList from '../contract-change-list/contract-change-list';
import ContractNewLicenseDialog from '../contract-new-license-dialog/contract-new-license-dialog';
import ContractStatus from '../contract-status/contract-status';
import { IContractGroup, IContractInfo, IContractLicense, ContractOrderTypesEnum, CurrencyEnum, IOrderItem, ISellableProducts, IContractItem, CheckboxLabel, LoadingButton, FocusTable, Alert, Skeleton } from '@frontend-workspace/react-shared-lib';
import FutureChangeIcon from '../../future-change-icon/future-change-icon';
import { t } from 'i18next';

/* eslint-disable-next-line */
export interface ContractLicensesProps {
  groups: Array<IContractGroup>,
  contractInfo: IContractInfo,
  saleDate: Date,
  orderItems: Array<IOrderItem>,
  sellableProducts?: Array<ISellableProducts>,
  nextInvoiceCost: number,
  recurringDiscount: number,
  isHandlingSale: boolean,
  onEditLicense: (licenseNumber: number) => void,
  onNewSale: () => void,
  onOpenMoveInvoice: () => void
}

export function ContractLicenses(props: ContractLicensesProps) {
  const dispatch = useSingleContractDispatch();
  const [isNewLicenseDialogOpen, setIsNewLicenseDialogOpen] = useState(false);
  const [showTerminatedLicenses, setShowTerminatedLicenses] = useState(false);
  const activeGroups = props.groups.filter(g => !showTerminatedLicenses ? g.licenses.length > 0 : g.licenses.length > 0 || g.terminatedLicenses.length > 0);
  const toggleNewLicenseDialogOpen = () => setIsNewLicenseDialogOpen(!isNewLicenseDialogOpen);
  const numberOfTerminatedLicenses = flatten(activeGroups.map(g => g.terminatedLicenses)).length;
  const handleSeatsChange = (value: number, licenseNumber: number, itemId: string) => {
    if (!isNewLicense(licenseNumber)) dispatch({ type: "SEATS_CHANGE", licenseNumber: licenseNumber, newValue: value, contractItemId: itemId });
    if (isNewLicense(licenseNumber)) dispatch({ type: "NEW_ITEM_SEATS_CHANGE", licenseNumber: licenseNumber, newValue: value, orderId: itemId })
  }
  return (<>
    <Row>
      <Col xs="auto">
        <div className='mb-4'>
          <LoadingButton
            loading={!props.sellableProducts}
            variant="contained"
            onClick={toggleNewLicenseDialogOpen}
            disabled={!props.sellableProducts}
            loadingPosition="start"
            startIcon={<Icon>add</Icon>}
            className='minButtonWidth'>
            {t("standard.newLicense")}
          </LoadingButton>
        </div>
      </Col>
      {numberOfTerminatedLicenses > 0 && <Col>
        <div className='mb-4'>
          <CheckboxLabel
            checked={showTerminatedLicenses}
            label={`${t("standard.viewExpiredLicenses")} (${numberOfTerminatedLicenses})`}
            onChange={() => setShowTerminatedLicenses(!showTerminatedLicenses)}
          />
        </div>
      </Col>}
    </Row>
    <Row>
      <Col md={12} xxl={8}>
        {activeGroups.map(g => {
          const groupVismaIds = g.licenses.reduce((vismaIds: Array<string>, l) => vismaIds.concat(l.items.map(i => i.vismaContractItemId)), []);
          const seatChangesInGroup = props.orderItems.filter(i => i.type === ContractOrderTypesEnum.AddSeats);
          const newLineChangesInGroup = props.orderItems.filter(i => i.groupId === g.id && i.type === ContractOrderTypesEnum.AddProductsToContract);
          const terminateItemsInGroup = props.orderItems.filter(i => groupVismaIds.includes(i.vismaContractItemId) && i.type === ContractOrderTypesEnum.TerminateLicense);
          const discountChangeItemsInGroup = props.orderItems.filter(i => groupVismaIds.includes(i.vismaContractItemId) && i.type === ContractOrderTypesEnum.ChangeRecurringDiscountPercent);
          return <ContractGroupTable
            key={g.id}
            group={g}
            showTerminatedLicenses={showTerminatedLicenses}
            seatChanges={seatChangesInGroup}
            newLineChanges={newLineChangesInGroup}
            terminateLicenseChanges={terminateItemsInGroup}
            discountChanges={discountChangeItemsInGroup}
            currency={props.contractInfo.currency}
            onSeatsChange={handleSeatsChange}
            onEditLicense={props.onEditLicense}
          />
        })}
        {activeGroups.length === 0 &&
          <Alert severity="info" className='my-3'>
            {t("standard.thereAreNoLicensesToDisplayHere")}
          </Alert>}
      </Col>
      <Col md={12} xxl={4}>
        <ContractStatus
          contractInfo={props.contractInfo}
          recurringDisount={props.recurringDiscount}
          nextInvoiceCost={props.nextInvoiceCost}
          onOpenMoveInvoice={props.onOpenMoveInvoice}
        />
        {/* TODO commented out in case we want to roll back from SalesForce. Remove if this is not needed */}
        {props.isHandlingSale ? <Skeleton
          height={300}
        /> : <ContractChangeList
          orderItems={props.orderItems.filter(o => !o.isExisting)}
          contractInfo={props.contractInfo}
          onNewSale={props.onNewSale}
          saleDate={props.saleDate}
        />}
      </Col>
    </Row>
    {isNewLicenseDialogOpen && props.sellableProducts &&
      <ContractNewLicenseDialog
        onClose={toggleNewLicenseDialogOpen}
        sellableProducts={props.sellableProducts}
        currency={props.contractInfo.currency}
      />}
  </>);
}

function ContractGroupTable(props: {
  group: IContractGroup,
  showTerminatedLicenses: boolean,
  seatChanges: Array<IOrderItem>,
  newLineChanges: Array<IOrderItem>,
  terminateLicenseChanges: Array<IOrderItem>,
  discountChanges: Array<IOrderItem>,
  currency: CurrencyEnum,
  onSeatsChange: (value: number, licenseNumber: number, itemId: string) => void,
  onEditLicense: (licenseNumber: number) => void
}) {
  const quantityRenderer = (params: ValueFormatterParams) => {
    let currentValue = params.value;
    const item = params.data.items[0];
    const seatChangesQuantity = props.seatChanges.filter(i => i.contractItemId === item.id).reduce((sum, i) => i.quantity + sum, 0);
    const lineChangesQuantity = props.newLineChanges.filter(i => i.licenseNumber === item.licenseNumber && !i.isModule).reduce((sum, i) => i.quantity + sum, 0);
    const hasMultipleLines = (lineChangesQuantity > 0 || params.data.items.length > 1) && !isNewLicense(params.data.licenseNumber);
    const hasFutureChange = props.seatChanges.find(i => i.isExisting && i.licenseNumber === item.licenseNumber) || props.newLineChanges.find(i => i.isExisting && i.licenseNumber === item.licenseNumber);

    if (hasFutureChange) return <div>{currentValue} (Har fremtidige endringer)</div>

    currentValue = currentValue + seatChangesQuantity;
    currentValue = currentValue + lineChangesQuantity;

    return hasMultipleLines ? <div>{currentValue} (Flere rader)</div> : <div>{currentValue}</div>
  }

  const nextInvoiceCostRenderer = (params: ValueFormatterParams) => {
    const modules: Array<IContractLicense> = params.data.modules;
    const newModuleItems = props.newLineChanges.filter(i => i.licenseNumber === params.data.licenseNumber && i.isModule);
    const allItems = flatten(modules.reduce((items: Array<any>, m) => items.concat(m.items), newModuleItems.concat([params.data.items])));
    const allLicenseNumbers = allItems.map(i => i.licenseNumber);
    let currentValue = allItems.reduce((sum, item) => {
      const discountChange = props.discountChanges.find(i => i.contractItemId === item.id);
      return sum + calcItemRecurringPrice(item, discountChange?.recurringDiscountPercent);
    }, 0);
    currentValue = props.seatChanges.filter(i => allLicenseNumbers.includes(i.licenseNumber)).reduce((sum, i) => calcItemRecurringPrice(i) + sum, currentValue);

    return <div>{toCurrency(currentValue, props.currency)}</div>;
  }

  const editRenderer = (params: ValueFormatterParams) => {
    const onClick = () => props.onEditLicense(params.data.licenseNumber);
    const item = params.data.items[0];
    const modules = params.data.modules.map((m: any) => m.licenseNumber);

    const seatChange = props.seatChanges.find(i => i.contractItemId === item.id);
    const terminateChange = props.terminateLicenseChanges.find(i => i.contractItemId === item.id);
    const hasChange = seatChange?.isExisting || terminateChange?.isExisting || isFutureLicense(item.licenseNumber);
    const hasModuleChange = props.seatChanges.find(i => modules.includes(i.licenseNumber)) || props.newLineChanges.find(i => modules.includes(i.licenseNumber));
    const isTerminated = !params.data.isActive && params.data.licenseNumber > 10;
    return (
      <div className='d-flex flex-row align-items-center'>
        <div>
          <PrimaryButton
            variant="outlined"
            size='small'
            onClick={onClick}
            disabled={hasChange || isTerminated}>
            {t("standard.change")}
          </PrimaryButton>
        </div>
        {hasModuleChange && <FutureChangeIcon item={hasModuleChange} isModule />}
        {seatChange && <FutureChangeIcon item={seatChange} />}
        {terminateChange && <FutureChangeIcon item={terminateChange} />}
      </div>
    );
  }

  const group = props.group;
  const licenseNumberRenderer = (params: ValueFormatterParams) => isNewLicense(params.value) ? isFutureLicense(params.value) ? "Fremtidig" : "Ny" : params.value;

  const productNameHeaderRenderer = (params: any) => <div className='ag-header-cell-label'>
    <img src={group.iconUrl} alt='' height={30} />
    <span className='ag-header-cell-text ms-2'>{params.displayName}</span>
  </div>;
  const recurringDiscountRenderer = (params: ValueFormatterParams) => {
    const item = params.data.items[0];
    const discountChange = props.discountChanges.find(i => i.contractItemId === item.id);
    return discountChange ? discountChange.recurringDiscountPercent : item.recurringDiscountPercent;
  }

  const productNameRenderer = (params: ValueFormatterParams) => {
    const modules: IContractLicense[] = params.data.modules;
    const isActive = params.data.isActive;
    const moduleNames = modules.map(m => m.productName);
    const lineChangeModuleNames = props.newLineChanges.filter(i => i.licenseNumber === params.data.licenseNumber && i.isModule).map(m => m.productName);
    const allModuleNames = moduleNames.concat(lineChangeModuleNames);
    const baseName = allModuleNames.length > 0 ? `${params.value} + ${allModuleNames.length}` : params.value;
    return `${baseName}`;
  }
  const unitCostRenderer = (params: ValueFormatterParams) => {
    const modules: IContractLicense[] = params.data.modules;
    const moduleItems = modules.reduce((items: IContractItem[], m) => items.concat(m.items), []);
    const newModuleItems = props.newLineChanges.filter(i => i.licenseNumber === params.data.licenseNumber && i.isModule);
    const allItems = moduleItems.concat(newModuleItems);
    const unitPrice = allItems.reduce((price, module) => module.recurringPrice + price, params.value);
    return toCurrency(unitPrice, props.currency);

  }
  const data = props.showTerminatedLicenses ? group.licenses.concat(group.terminatedLicenses) : group.licenses;
  const getRowStyle = (params: any) => {
    if (!params.data.isActive && params.data.licenseNumber > 1000) return { background: '#FEECEB' };
    return null;
  };

  return <div key={group.id} className='mb-4'>
    <FocusTable
      headers={CreateHeaders([
        textHeader("productName", group.groupName, HeaderSizeEnum.LARGE, { renderer: productNameRenderer, headerRenderer: productNameHeaderRenderer }),
        textHeader("itemType", "Type", HeaderSizeEnum.SMALL),
        booleanHeader("isMaintenance", t("standard.maintenance")),
        textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.SMALL, { renderer: licenseNumberRenderer }),
        textHeader("numberOfSeats", t("standard.quantity"), HeaderSizeEnum.SMALL, {
          renderer: quantityRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        currencyHeader("unitCost", t("standard.unitCost"), HeaderSizeEnum.MEDIUM, {
          renderer: unitCostRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("recurringDiscountPercent", t("standard.recurringDiscountPercent"), HeaderSizeEnum.SMALL, {
          renderer: recurringDiscountRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("nextInvoiceCost", t("standard.totalPrice"), HeaderSizeEnum.MEDIUM, {
          renderer: nextInvoiceCostRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("", "", HeaderSizeEnum.MEDIUM, {
          renderer: editRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        })
      ])}
      data={data}
      hidePagination
      key={group.id}
      disableSearch={true}
    />
  </div >
}


export default ContractLicenses;